'use client';

import Backdrop from '@mui/material/Backdrop';
import { clsx } from 'clsx';
import { FC, useEffect, useMemo, useState } from 'react';
import { Icon, IconType } from 'atoms/Icon';
import { Link } from 'atoms/Link/Link';
import { SubMenu } from 'organisms/Header/Navigation/SubMenu';
import { TRANSITION_DURATION } from 'modules/framer-motion/constants';
import { gtmPushShopNavigation } from 'modules/gtm/modules/sections/navigation/navigation';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { usePathname } from 'modules/next/hooks';
import { usePopoverManager } from 'modules/shop/popoverManager/hooks';
import { PopoverType } from 'modules/shop/popoverManager/types';
import { AppRoutes } from 'modules/shop/routes';
import {
  useActiveNavigationItem,
  useSiteConfig,
} from 'modules/shop/siteConfig/hooks';
import { NavigationItem } from 'modules/stroyblok/types';
import { CartIcon } from '../CartIcon';
import { NAVIGATION_ID } from '../constants';
import { Flyout } from './Flyout';
import { ItemLevel1 } from './ItemLevel1';
import { LoginIcon } from './LoginIcon';
import styles from './Navigation.module.css';

export const Navigation: FC = () => {
  const pathname = usePathname();
  const t = useTranslations();
  const { openPopover, closePopover } = usePopoverManager();
  const { header } = useSiteConfig();
  const { isTablet: isMoreButton } = useMediaQuery();

  const gtmAccountEventLabel = t('header.gtmNavigationLabels.accountIcon');
  const gtmSearchEventLabel = t('header.gtmNavigationLabels.searchIcon');
  const gtmHomeButtonEventLabel = t('header.gtmNavigationLabels.HomeButton');

  const activeItem = useActiveNavigationItem(header);

  const [state, updateState] = useState<{
    visibleItem: NavigationItem | null;
    showMore: boolean;
    isSticky?: boolean;
  }>({
    visibleItem: null,
    showMore: false,
  });

  const navigationMoreItems = useMemo(
    () =>
      header?.children?.filter(
        (navigationItem) => navigationItem.showOnTablet !== true,
      ) || [],
    [header, isMoreButton],
  );

  const handleToggleShowMore = () => {
    updateState((s) => ({ ...s, showMore: !s.showMore, visibleItem: null }));
    closePopover();
  };

  const handleCloseShowMore = () => {
    updateState((s) => ({ ...s, showMore: false }));
  };

  const backdropOnClose = () => {
    updateState((s) => ({ ...s, showMore: false, visibleItem: null }));
  };

  useEffect(() => {
    document.body.style.overflow = Boolean(state.visibleItem)
      ? 'hidden'
      : 'initial';
  }, [state.visibleItem]);

  useEffect(() => {
    updateState((s) => ({ ...s, showMore: false, visibleItem: null }));
  }, [pathname]);

  useEffect(() => {
    const eventHandler = () => {
      // 160 - manually get from origin site
      updateState((s) => ({ ...s, isSticky: window.scrollY > 160 }));
    };

    eventHandler();

    window.addEventListener('scrollend', eventHandler);

    return () => {
      window.removeEventListener('scrollend', eventHandler);
    };
  }, []);

  const handleHomeButtonClick = () => {
    gtmPushShopNavigation(gtmHomeButtonEventLabel);
  };

  const handleSearchIconClick = () => {
    gtmPushShopNavigation(gtmSearchEventLabel);
  };

  return (
    <>
      <div className={clsx(styles.root, state.isSticky && styles.sticky)}>
        <nav className={styles.nav} id={NAVIGATION_ID}>
          <ul className={styles.navLinks}>
            <li className={clsx(styles.navLink, styles.home)}>
              <Link href={AppRoutes.HOME} onClick={handleHomeButtonClick}>
                <Icon type={IconType.home} className={styles.homeIcon} />
              </Link>
            </li>

            {header?.children?.map((item: NavigationItem) => {
              const isOpen = state.visibleItem?._uid === item._uid;
              const isActive = activeItem?._uid === item._uid;

              return (
                <li
                  key={item._uid}
                  className={clsx(
                    styles.navLink,
                    isOpen && styles.open,
                    isActive && styles.navActive,
                    !item.showOnTablet && styles.hideOnTablet,
                  )}
                  onClick={handleCloseShowMore}
                >
                  <ItemLevel1
                    item={item}
                    setVisibleItem={() => {
                      closePopover();
                      updateState((s) => ({
                        ...s,
                        visibleItem: isOpen ? null : item,
                      }));
                    }}
                  />
                </li>
              );
            })}
            {isMoreButton && navigationMoreItems && (
              <li
                className={clsx(
                  styles.navLink,
                  state.showMore && styles.active,
                  styles.wrapperMoreButton,
                )}
              >
                <button
                  className={styles.moreButton}
                  onClick={handleToggleShowMore}
                >
                  {t('header.buttons.more')}{' '}
                  <Icon
                    type={IconType.arrowDown}
                    className={clsx(
                      styles.moreIcon,
                      state.showMore && styles.rotate,
                    )}
                  />
                </button>
              </li>
            )}
          </ul>
          <div className={styles.rightIcons}>
            <Link href={AppRoutes.SEARCH} onClick={handleSearchIconClick}>
              <Icon type={IconType.search} />
            </Link>
            <LoginIcon
              onClick={() => {
                openPopover(PopoverType.LOGIN);
                backdropOnClose();
                gtmPushShopNavigation(gtmAccountEventLabel);
              }}
            />
            <CartIcon
              onClick={() => {
                openPopover(PopoverType.CART);
                backdropOnClose();
              }}
            />
          </div>
        </nav>
        <div className={styles.wrapper}>
          <div className={styles.wrapperInner}>
            {state.showMore && (
              <SubMenu
                navigationItems={navigationMoreItems}
                setVisibleItem={(visibleItem: NavigationItem | null) => {
                  updateState((s) => ({ ...s, visibleItem }));
                }}
                visibleItem={state.visibleItem}
                activeItem={activeItem}
              />
            )}
            <Flyout item={state.visibleItem} />
          </div>
        </div>
      </div>
      <Backdrop
        open={Boolean(state.visibleItem) || state.showMore}
        classes={{ root: styles.backdrop }}
        onClick={backdropOnClose}
        transitionDuration={TRANSITION_DURATION}
      />
    </>
  );
};
